import React, { useState } from 'react';
import { Box } from '@mui/material';

// TKT-236 follow-up: the live tracking page renders one mini-map per truck
// (~24 cards). The previous implementation spun up a full interactive
// mapbox-gl (WebGL) map per card, which blew past the browser's hard cap of
// ~16 simultaneous WebGL contexts ("Too many active WebGL contexts. Oldest
// context will be lost."), so roughly half the maps rendered blank/broken.
//
// These cards only need a static thumbnail with a marker, so we now use the
// Mapbox Static Images API: a single <img> per card, zero WebGL contexts, and
// it renders reliably no matter how many trucks are on screen.

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN
  || 'pk.eyJ1IjoiZGVtby1hY2NvdW50IiwiYSI6ImNrOHV0Zm9qMTAwZmEzb3BnbHNpbGIyZXEifQ.VLdlIGh1EWfVV7jz2v7QyA';

const getStatusColor = (status) => {
  switch (status) {
    case 'driving': return '2196F3'; // Blue
    case 'working': return 'FF9800'; // Orange
    case 'idle': return 'FFC107'; // Yellow
    case 'parked': return '4CAF50'; // Green
    default: return '757575'; // Gray
  }
};

const MapboxMiniMap = ({
  latitude,
  longitude,
  status = 'offline',
  height = '120px',
  truckName = 'Truck'
}) => {
  const [imgError, setImgError] = useState(false);

  const hasCoords = latitude != null && longitude != null
    && !Number.isNaN(Number(latitude)) && !Number.isNaN(Number(longitude));
  const hasToken = MAPBOX_TOKEN && !MAPBOX_TOKEN.includes('demo');

  const fallback = (message) => (
    <Box
      role="img"
      aria-label={`${truckName} ${message}`}
      sx={{
        height,
        width: '100%',
        minHeight: '120px',
        backgroundColor: '#f5f5f5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '8px',
        border: '1px solid #e0e0e0',
        color: '#757575',
        fontSize: '12px',
        textAlign: 'center',
        px: 1
      }}
    >
      {message}
    </Box>
  );

  if (!hasCoords) {
    return fallback('No GPS Data');
  }

  if (!hasToken || imgError) {
    return fallback(imgError ? 'Map unavailable' : 'Map ready (add Mapbox token)');
  }

  const lon = Number(longitude);
  const lat = Number(latitude);
  const zoom = 14;
  // Request a retina thumbnail large enough to stay crisp in the larger
  // detail-modal map too; objectFit: cover crops it to whatever box it lands in.
  const width = 600;
  const imgHeight = 400;
  const marker = `pin-s+${getStatusColor(status)}(${lon},${lat})`;
  const staticUrl = `https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/`
    + `${marker}/${lon},${lat},${zoom},0/${width}x${imgHeight}@2x`
    + `?access_token=${MAPBOX_TOKEN}`;

  return (
    <Box
      className="mapbox-container mapbox-mini"
      data-testid="mapbox-mini-container"
      sx={{
        height,
        width: '100%',
        minHeight: '120px',
        borderRadius: '8px',
        overflow: 'hidden',
        border: '1px solid #e0e0e0',
        position: 'relative'
      }}
    >
      <img
        src={staticUrl}
        alt={`Map showing the location of ${truckName}`}
        onError={() => setImgError(true)}
        loading="lazy"
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block'
        }}
      />
    </Box>
  );
};

export default MapboxMiniMap;
